import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Model from 'flarum/common/Model';
import User from 'flarum/common/models/User';
import Modal from 'flarum/common/components/Modal';
import ModalManager from 'flarum/common/components/ModalManager';
import ModalManagerState from 'flarum/common/states/ModalManagerState';

// (Phase D1): 跨扩展共享上传方法 —— **纯副作用 import, 不取任何绑定**
//   为什么必须是「永久」的这一行: flarum-webpack-config **没有**任何「强制把
//   common/ 模块打进 bundle」的机制 —— 没人 import 的模块会被 tree-shaking 干掉。
//   实测: 只加文件不加 import 时 `npm run build` 报 asset forum.js
//   "compared for emit" (即产物与上次完全一致), md5 一个字节没变。
//   而这个模块存在的**全部意义**就是被别的扩展用
//   `ext:ziiven/ziven-core/common/helpers/uploadFile` 取到 —— 被摇掉 = 功能不存在。
//   autoExportLoader 会在模块末尾注入 `flarum.reg.add('ziiven-ziven-core',
//   'common/helpers/uploadFile', {...})`, side-effect import 正是保住这一句所需。
//   真正用到它的 Phase D2 调用方 (7 个直传点) 是**别的扩展**, 本扩展自己用不到,
//   所以这行 import 会一直存在 —— 不是临时的。
import '../common/helpers/uploadFile';

// (辉哥 2026-10-19 拍板方案 A): 「modal 滚动位置守门」JS 半边 ——
//   修: Flarum 2.0 移动端 (iOS) 打开任意 modal 后, 底层页面跳回顶部。
//   根因: core 依赖的 body-scroll-lock@^4.0.0-beta.0 的 ESM build 写
//     `body.style.top = -scrollY` (缺 px 单位) ⇒ CSSOM 直接丢弃 ⇒ body 变
//     position:fixed 但 top 无效 ⇒ body 脱离文档流 ⇒ scrollHeight 塌缩
//     ⇒ scrollY 被 clamp 到 0。详见 js/src/forum/utils/modalScrollGuard.js 头注释。
//
//   为什么这行 import 是**必须的** (跟上面 uploadFile 同一个坑):
//     flarum-webpack-config **没有**任何「强制把模块打进 bundle」的机制 ——
//     没人 import 的模块会被 tree-shaking 摇掉 (build 会报 asset forum.js
//     "compared for emit", 产物 md5 一个字节不变)。本模块存在的全部意义就是
//     靠这行 side-effect import 被摇不掉。
//
//   🔴 移除条件: 跑 `python3 tools/modal-scroll-guard-probe.py`, 若探针输出
//     「不跳了 ⇒ 官方已修, 本补丁可以移除」, 就把本行 + modalScrollGuard.js
//     + less/forum.less 末尾的段 **三处一起删掉**。
//     (那个 focus 修法不要动 —— 那是另一件事)
import './utils/modalScrollGuard';

// (辉哥 2026-10-24 22:0x 拍板 B 方案): 全站弹窗默认**只**能靠「右上角关闭按钮」
// 或「Esc 键」关闭, 点弹窗后面的遮罩层不再关闭。
// 机制与取舍详见 js/src/common/utils/modalBackdropClose.js 顶部注释。
// ⚠️ forum 栈与 admin 栈各有一份 Modal 基类 ⇒ admin/index.js 里也要装一次。
import { installModalBackdropClose } from '../common/utils/modalBackdropClose';
import IgnoreSearchUser from './IgnoreSearchUser';
import IgnoreMentions from "./IgnoreMentions";
import modifyDiscussionItem from './modifyDiscussionItem';
import modifyTagSelectionModal from './modifyTagSelectionModal';
import modifyNavigation from './modifyNavigation';
import modifyApplication from './modifyApplication';
import modifyHeaderSecondary from './modifyHeaderSecondary';
import modifyDrawer from './modifyDrawer';
// (辉哥 2026-09-09 19:25 反馈 "把用户页改动迁到 ziven core"):
//   从 ziven-bjxy-website 迁过来
//   改 UserPage.navItems + 覆盖 PostsUserPage.prototype.content 渲染默认显示讨论
//   删 posts tab 给非 admin/mod, mobile top bar 强制显示当前 active tab 文字
import modifyUserPage from './modifyUserPage';
// (Trello 卡 6a7e65df8eb1684c9d8b78f8): 手机号登录 extend 覆盖 vendor LogInModal
import phoneLoginExtension from './components/PhoneLogin';
// (Trello 卡 6a7fc343f990e73904af2c75): 手机号注册 extend 覆盖 vendor SignUpModal
import signUpExtension from './components/SignUp';
// (Trello 卡 6a7fcee8778d13c624d2acdc, 辉哥 11:51 拍板 go): 手机号找回密码 extend 覆盖 vendor ForgotPasswordModal
import forgotPasswordExtension from './components/ForgotPassword';
// (Trello 卡 6aab90b1e1dc16fd9ba865c0, 辉哥 9/17 15:01 拍板):
//   详情页头图功能 — override DiscussionPage.prototype.hero, 有图时返回
//   [<DiscussionPageHero />, <DiscussionHero />], 无图时返回 vendor 默认
import extendDiscussionPage from './extendDiscussionPage';
// (辉哥 2026-09-23 08:38 拍板 a 方案, Trello 卡 trFWS1CK / 6ab31fb50aa966f6f853935c):
//   ziven-core override vendor TerminalPost view() 末尾追加最后回复用户头像 + 用户链接
//   根因: vendor Flarum 2.0 replied_text 只含 {username} placeholder, 不渲染 <a href> 和 <Avatar>
//   zpc 适用 — bjxy.top + geek.ski + 其他 ziven 部署都修复
import modifyTerminalPost from './modifyTerminalPost';
// (同上): DiscussionComposer extend — 加 hero image 上传按钮
//   Admin + Mods 默认有 uploadHeroImage 权限才显示, 普通用户 composer 不显示
import modifyDiscussionComposer from './modifyDiscussionComposer';

// (辉哥 9/17 16:11 派 Coder 修, V 测 r.18b 仍 FAIL 真因):
//   boot 时 lazy module 未 ready, 直接 `extend(XXX.prototype, ...)` 或 `XXX.prototype.yy = ...`
//   抛 `Cannot read properties of undefined (reading 'prototype')`, 阻塞整个
//   initializer IIFE 后续执行, hero extend (跟其他 12+ 个 extends) 没注册
// (Trello 卡 F3b3PgCM 的 synopsis 摘要过滤补丁 —— **2026-10-26 已整体移除**)
//   移除原因: vendor fof/synopsis 升到 2.0.1 后上游自己在服务端把原始 BBCode /
//   图片 URL 整块删掉了 ⇒ 这层客户端补丁冗余 (辉哥 12:0x 拍板: 上游修了就移除)。
//   原实现文件 js/src/forum/modifySynopsisExcerpt.js + js/src/forum/utils/stripFormatting.js
//   已删除, 下面这行 import 一并移除。留档细节见文件下方「synopsis 摘要过滤补丁」段。
//
//   r.18b 修法只包了 1 处 User.prototype.ignore_all, 漏了 12+ 个其他 lazy module 调用
//
//   修法 A: safeExtend + safeOverride helper (function declaration 自动 hoist)
//     - target undefined 或 prototype 缺失 → console.warn 跳过, 不阻塞后续 extends
//     - extend/override 内部抛错 → console.warn 跳过, 不阻塞后续 extends
//     - 挂 window.__zivenCoreSafeExtend/Override 给 sub-files 使用
//   修法 B (fallback): sub-files 用 flarum.reg.onLoad() 等 module ready 再 extend
//     (PhoneLogin 已经在用 onLoad pattern, 见 components/PhoneLogin.js L42-72)
//
//   实战教训 (跨 ziven 扩展适用 SOP 补遗):
//   - Flarum 2.0 vendor class 走 lazy chunk 加载, `import X from 'flarum/forum/components/X'`
//     在 chunk 未 load 时 X 是 undefined, 直接 `extend(X.prototype, ...)` 抛错
//   - 这个错阻塞后续所有 extends (Flarum 不自动 try/catch)
//   - 必须用 safeExtend/safeOverride 兜底, 不能依赖具体某个 module 的 patch try/catch
//   - 任何 ziven 扩展如果 lazy extend vendor class, 都必须用 safeExtend 替代 extend
function safeExtend(targetOrClass, method, fn) {
  try {
    // 兼容 Class (typeof === 'function' 走 .prototype) 或 prototype 对象
    // 关键: 把 `targetOrClass()` 调用留在 safeExtend 内 (try/catch 兜底)
    // 如果调用方写 `safeExtend(X().prototype, ...)`, X() undefined 时表达式本身抛错
    // 绕过了 try/catch, 必须改为 `safeExtend(X(), ...)` 让 X() 在 try 内求值
    const target = typeof targetOrClass === 'function' ? targetOrClass.prototype : targetOrClass;
    if (!target || typeof target !== 'object') {
      console.warn('[ziven-core] safeExtend skipped (target undefined):', method);
      return null;
    }
    return extend(target, method, fn);
  } catch (e) {
    console.warn('[ziven-core] safeExtend failed:', method, e && e.message);
    return null;
  }
}
function safeOverride(targetOrClass, method, fn) {
  try {
    const target = typeof targetOrClass === 'function' ? targetOrClass.prototype : targetOrClass;
    if (!target || typeof target !== 'object') {
      console.warn('[ziven-core] safeOverride skipped (target undefined):', method);
      return null;
    }
    return override(target, method, fn);
  } catch (e) {
    console.warn('[ziven-core] safeOverride failed:', method, e && e.message);
    return null;
  }
}

// 挂 global 让 sub-files 用 (PhoneLogin.js 已用 window.flarum.reg 同样 pattern)
// function declaration 自动 hoist 到模块顶层, sub-files 在自己的 export function 内取 window.__zivenCoreSafeExtend 时,
// index.js IIFE 顶部已执行过赋值
window.__zivenCoreSafeExtend = safeExtend;
window.__zivenCoreSafeOverride = safeOverride;

// FancyBox 本地路径 (6.x 需要加载多个 CSS 文件)
const FANCYBOX_CSS_PATHS = ['/assets/extensions/ziiven-ziven-core/fancyBox/fancybox.css'
// '/assets/extensions/ziiven-dress-up/fancybox/carousel.css',
// '/assets/extensions/ziiven-dress-up/fancybox/fullscreen.css'
];
const FANCYBOX_JS = '/assets/extensions/ziiven-ziven-core/fancyBox/fancybox.umd.js';
let fancyboxLoaded = false;
let fancyboxLoading = false;

// (辉哥 2026-09-14 09:18 拍板): 帖子详情页所有图片 fancybox 弹窗
//   vendor Flarum 2.0 CommentPost 渲染 <div className="Post-body"> 包 contentHtml
//   contentHtml 包含 <img src="..."> (来自 Markdown / fof/upload / 用户手动插入)
//   vendor Flarum 2.0 + 7 个 ziven 扩展 + fof 系列**没**做 lightbox (s9e/text-formatter 只做 youtube 嵌入)
//   修法: jQuery delegate 绑到 document, 匹配 '.Post-body img', 弹多图 gallery
//   jQuery delegate 模式 listener 绑在 document, 任何 Post-body img click 都走这里
//   mithril redraw 实时 reply / 实时编辑 mount 新 Post-body 时, jQuery delegate 自动 capture
//   不需要 MutationObserver 兜底, 跟 r.12.h.1 delegate 模式一样
//   实战教训 (SOP 9/11 r.12.h.1): 任何 mithril Component extend 绑 jQuery event 必须用 delegate 模式
//     直接 $elem.on() listener 绑在老 element, mithril redraw 重新创建的新 element 没 listener
//     jQuery delegate 模式 listener 绑在 root, click event 冒泡到 root 时 jQuery 检查 event.target
//     是不是匹配 selector, 匹配就跑 handler. 新 element 创建后仍能 match selector 跑 handler
//   r.12.h.3 时代 iPhone 真机触摸 race condition 是 toggle click 跟 vendor mithril onclick 抢 capture phase
//     .Post-body img vendor 没绑 click, jQuery delegate 直接 work, 不需要抢 phase
//   ziven-bjxy-website 评价多图 (BjxyPage.jsx L174-197) 走 Fancybox.show([...urls...]) 字符串数组
//     但不在 .Post-body 段 (在 ziven-bjxy-website 自己的页面), 0 冲突
//   单例保护 __zivenCorePostBodyImgFancyboxBound 防止 ziven-core 多次 init 重复绑定
function setupPostBodyImgFancybox() {
  if (!window.jQuery) return;
  if (window.__zivenCorePostBodyImgFancyboxBound) return;
  window.__zivenCorePostBodyImgFancyboxBound = true;

  // jQuery delegate 模式: 绑到 document, 任何 Post-body img click 都走这里
  // mithril redraw 实时 reply / 实时编辑 mount 新 Post-body 时, 自动 capture
  // ziven-bjxy-website 评价多图不在 .Post-body 段, 不会冲突
  $(document).on('click.zivenCorePostBodyImg', '.Post-body img', function (e) {
    e.preventDefault();
    e.stopPropagation();
    const $img = $(this);
    // 找同一个 Post-body 段的所有 img 拼成 gallery
    const $post = $img.closest('.Post-body');
    if ($post.length === 0) return;
    const $imgs = $post.find('img');
    const sources = $imgs.map(function () {
      return {
        src: $(this).attr('src') || '',
        type: 'image'
      };
    }).get();
    const index = $imgs.index(this);
    if (sources.length === 0) return;
    if (window.Fancybox && typeof window.Fancybox.show === 'function') {
      // 优先 Fancybox.show 对象数组 (支持 startIndex, 跟 fancybox API 一致)
      window.Fancybox.show(sources, {
        startIndex: index
      });
    } else if (window.app && typeof window.app.fancyboxOpen === 'function') {
      // 兜底: 单图模式
      window.app.fancyboxOpen(sources[index].src);
    }
  });
}

// 动态加载 FancyBox 资源
function loadFancyBox() {
  if (fancyboxLoaded || fancyboxLoading) return;
  fancyboxLoading = true;

  // 加载所有 CSS 文件
  FANCYBOX_CSS_PATHS.forEach(function (cssPath) {
    if (!document.querySelector('link[href="' + cssPath + '"]')) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssPath;
      document.head.appendChild(link);
    }
  });

  // 加载 JS
  if (!document.querySelector('script[src="' + FANCYBOX_JS + '"]')) {
    var script = document.createElement('script');
    script.src = FANCYBOX_JS;
    script.onload = function () {
      fancyboxLoaded = true;
      fancyboxLoading = false;
      console.log('FancyBox loaded successfully');
      // 加载完 fancybox 后, 注册 Post-body img click delegate
      setupPostBodyImgFancybox();
    };
    script.onerror = function (e) {
      fancyboxLoading = false;
      console.error('FancyBox load error:', e);
    };
    document.head.appendChild(script);
  } else {
    fancyboxLoaded = true;
    // 已有 script 标签 (重复 init), 也注册 delegate
    setupPostBodyImgFancybox();
  }
}

// 打开 FancyBox 预览
function openFancyBox(src, options) {
  options = options || {};

  // 确保 FancyBox 已加载
  if (!window.Fancybox) {
    loadFancyBox();
    // 延迟执行，等待加载完成
    var attempts = 0;
    var checkInterval = setInterval(function () {
      attempts++;
      if (window.Fancybox) {
        clearInterval(checkInterval);
        doOpenFancybox(src, options);
      } else if (attempts > 50) {
        clearInterval(checkInterval);
        console.error('FancyBox load timeout');
      }
    }, 100);
  } else {
    doOpenFancybox(src, options);
  }
}

// 执行打开 FancyBox
function doOpenFancybox(src, options) {
  if (window.Fancybox) {
    window.Fancybox.show([{
      src: src,
      type: 'image'
    }], {
      dragToClose: false
    });
  }
}

// (辉哥 9/10 20:25 拍板): 滚动条颜色/宽度 改成后台可设置
//   ziven-core 后台"设置" tab 配 scrollbar_width / scrollbar_color / scrollbar_hover_color
//   forum 端读 setting, 动态 inject <style id="ziven-core-scrollbar-style"> 覆盖 less 默认值
//   优先级: 1) admin setting  2) less default (#1E40AF / #1E3A8A / 6px)
//   注: app.forum.attribute() 走 vendor ForumResource serializer,
//       src/Api/ForumAttributes.php 注册了 zivenCoreScrollbar{Width,Color,HoverColor} 3 个 attribute
//       Flarum 2.0 /api 接口自动返这些 camelCase keys
function injectScrollbarStyle() {
  // (辉哥 9/17 20:02 派 Coder 修 r.18h):
  //   app.forum 在 ziven-core IIFE 第一次跑时还没 ready (ForumState 异步 load),
  //   直接调 app.forum.attribute() 抛 TypeError. 之前 try/catch 兜底 + console.warn 'Cannot read properties of undefined (reading attribute)'
  //   现在改成 silent: app.forum undefined 时直接 return, 不抛错也不 console.warn
  if (!app.forum) return;
  try {
    const width = app.forum.attribute('zivenCoreScrollbarWidth') || 6;
    const color = app.forum.attribute('zivenCoreScrollbarColor') || '#1E40AF';
    const hoverColor = app.forum.attribute('zivenCoreScrollbarHoverColor') || '#1E3A8A';
    let styleEl = document.getElementById('ziven-core-scrollbar-style');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'ziven-core-scrollbar-style';
      document.head.appendChild(styleEl);
    }
    const radius = Math.max(2, Math.floor(parseInt(width) / 2));
    // (辉哥 9/18 15:17 反馈桌面端没生效): 去掉 @media (max-width: 767px) { } wrapper
    //   - 之前 r.18h 桌面端不生效真因: scrollbar CSS 被 @media 767px 包住, 只移动端生效
    //   - ziven-theme-aura 桌面 sidebar .App-drawer-aura (less L441-451) 用 var(--aura-border)
    //     优先级比 ziven-core * 高, 不会被覆盖
    //   - 全局 scrollbar CSS 应用到所有其他滚动元素 (帖子详情 / 用户页 / admin 等)
    //   - 桌面端 Chrome / Safari / Edge 用 ::-webkit-scrollbar, Firefox / W3C 用 scrollbar-width + scrollbar-color, 两条都写覆盖到位
    styleEl.textContent = "\n::-webkit-scrollbar { width: ".concat(width, "px; height: ").concat(width, "px; }\n::-webkit-scrollbar-track { background: transparent; }\n::-webkit-scrollbar-thumb { background: ").concat(color, "; border-radius: ").concat(radius, "px; }\n::-webkit-scrollbar-thumb:hover { background: ").concat(hoverColor, "; }\n* { scrollbar-width: thin; scrollbar-color: ").concat(color, " transparent; }\n    ").trim();
  } catch (e) {
    // 兜底, 不抛错影响 ziven-core 整体初始化
    console.warn('[ziven-core] injectScrollbarStyle failed:', e);
  }
}

// (辉哥 9/17 20:02 派 Coder 修 r.18h bjxy.top SPA hydrate fail 真因):
//   之前本地 r.18d rebuild dist 后, 我手动 cp dist/forum.js → public/assets/forum.js,
//   覆盖了 vendor ForumApplication 489KB bundle, 导致 public/assets/forum.js 不再 define
//   window.app = app (vendor ForumApplication 才有这个 register), SPA 调 app.load(data) 报
//   'app.load is not a function' throw, mithril 永远不 mount, 整个 forum 退到 <noscript> fallback
//
//   真因: ziven-core dist/forum.js 必须 cp 到 public/assets/extensions/ziiven-ziven-core/forum.js
//   (extension 子目录), 让 assets:publish lazy rebuild 时 vendor + extensions concatenate 进
//   public/assets/forum.js (1.4MB).
//
//   实战教训沉淀 (跨 ziven 扩展适用 SOP 补遗):
//   - ziven-core dist/forum.js 永远 cp 到 public/assets/extensions/<extension-id>/forum.js, 不要 cp 到 public/assets/forum.js
//   - assets:publish 后会 lazy rebuild public/assets/forum.js (vendor + all extensions concat),
//     通过 rev-manifest.json cache busting, 浏览器触发新 version 时 concat
//   - 直接 cp dist 到根 forum.js 会覆盖 vendor 489KB bundle, 引发 SPA hydrate fail
//   - 跨 ziven 扩展适用: rebuild dist 后必看 public/assets/forum.js size, 应该 1+ MB (vendor 489KB + ext 多个)
//     如果只 91KB 说明 cp 到错误位置
//
//   这个 IIFE probe 是 r.18h Coder 自测用的 (verify ziven-core IIFE 顺序), 不是 diagnostic,
//   production 不需要 (辉哥 SOP priority 1) — 移除

// ── synopsis 摘要过滤补丁 —— **2026-10-26 已整体移除** ──
// (原 Trello 卡 F3b3PgCM; 原实现 = js/src/forum/modifySynopsisExcerpt.js
//  + js/src/forum/utils/stripFormatting.js, 两个文件已删除)
//
//   移除原因: vendor fof/synopsis 升到 2.0.1 (2026-10-23) 后, **上游自己在服务端**
//   `src/Api/AddDiscussionExcerptField.php` 加了 IMAGE_TAGS
//   (IMG / UPL-IMAGE-PREVIEW / UPL-IMAGE) 整块删除 + removeFormatting,
//   ⇒ 原始 BBCode / 图片 URL 不再漏进摘要:
//     生产实测 disc#13 首帖摘要在 rc.2 是
//     `[upl-image-preview uuid=… url=…] 每年一到十一月…` → 2.0.1 变成
//     `每年一到十一月…` (原始 BBCode 整段消失), disc#12 结尾的裸图 URL 也被替掉。
//   ⇒ 这层客户端补丁变成冗余 (辉哥 2026-10-26 12:0x 拍板: 上游修了就移除)。
//
//   ⚠️ 已知残留边界 (留档, 供日后判断要不要补):
//   - 上游只覆盖 3 个**图片**标签; fof/upload 的非图片上传
//     `[upl-text-preview …]` / `[upl-file …]` 理论上仍会漏 ⇒ 实测生产库这两类 0 条。
//   - 上游是对**自己渲染出来的**元素整块删除; 用户正文里手打的 `[b]` 之类,
//     Flarum 2.0 存成 `<s>[b]</s>` / `<e>[/b]</e>` ⇒ 早被 removeFormatting 去掉。
//
//   原来的注册方式 (r.18aba 教训, 别再踩): priority -100 的 initializer 必须
//   在 module-load top level 跟 'ziven-core' initializer 并列注册, 塞进
//   'ziven-core' callback 内 add() 会被 toArray() 快照漏掉, 永远不跑。

// ── flatpickr 加载器 (辉哥 2026-10-22 21:07 拍板 ① 消除重复注入 + ③ locale 自检告警) ──
//   ⚠️ forum 栈与 admin 栈各有一份 —— 两个 bundle 互不共享模块, **改一处要同步另一处**。
//
//   三条硬事实 (决定了下面这段必须这么写, 别"简化"):
//   1) flatpickr.js 末尾 `window.flatpickr = I` ⇒ **每执行一次就整体替换
//      window.flatpickr**（l10ns 一起重置）⇒ 同一页里加载两次 flatpickr.js =
//      先前挂上的 l10ns.zh 被无声清掉（生产实测 l10ns 键只剩 en / default）。
//   2) zh.js 是 UMD: `var fp = window.flatpickr ?? {l10ns:{}}` ⇒ 它若早于
//      flatpickr.js 执行, l10ns.zh 会挂到临时对象上 ⇒ **静默丢失**。
//      ⇒ zh.js 必须严格排在 flatpickr.js 之后。
//   3) 动态插入的 <script> 默认 async ⇒ 多个包各自插入的脚本**执行顺序不保证**
//      ⇒ 只能用 onload 链保证「自己的 zh.js 在自己的 flatpickr.js 之后」。
//
//   本包只做这三件事 (⛔ 不改 vendor 文件 / ⛔ 不 monkey patch window.flatpickr):
//   ① 注入前先探测: window.flatpickr 已存在 ⇒ 不再重复加载 (不重置 l10ns)
//   ② 自己按顺序加载 zh.js (flatpickr.js 的 onload 里再插 zh.js)
//   ③ zh.js 就绪后自检 locale: l10ns.default 不是中文 ⇒ console.warn 一句中文
//
//   ⚠️ ④ 「实例被换掉」自愈 (peer BJXY session 2026-10-22 22:0x 提的风险 ②):
//   vendor flatpickr.js 末尾 `window.flatpickr = I` ⇒ **别人每加载一次 flatpickr.js
//   就整体换掉 window.flatpickr**（新实例的 l10ns 只有 en / default）。
//   而 zh.js 挂的是「它执行那一刻的当前实例」⇒ 挂在旧实例上的 l10ns.zh 会跟着旧实例
//   一起消失, **零报错、零日志**。
//
//   ⛔ 早插 zh.js 的坑（动态插入的 <script> **不阻塞 window 的 load 事件** ——
//   HTML 规范: insert-a-script 置 force-async, 不置 delay-the-load-event 标志
//   ⇒ window.load 可能在 flatpickr.js 还在下载时就触发):
//   若那时就插 zh.js ⇒ zh.js 早于 flatpickr.js 执行 ⇒ UMD 走到
//   `window.flatpickr ?? {l10ns:{}}` 分支 ⇒ l10ns.zh 挂到**临时对象**上 ⇒ 永久丢失。
//   ⇒ 「就绪」**不记**布尔、**不记**实例, 一律**现算**: 当前实例上真的有 l10ns.zh 才算就绪。
//     这样「实例被换掉」与「zh 挂错实例」两种情况都自动判为「未就绪」⇒ 允许重挂。
const FLATPICKR_JS = '/assets/extensions/ziiven-ziven-core/flatpickr/flatpickr.js';
const FLATPICKR_ZH_JS = '/assets/extensions/ziiven-ziven-core/flatpickr/zh.js';
// 只用于「zh.js 正在飞」的并发去重（就绪与否一律现算, 不用它判就绪）
let flatpickrZhLoading = false;
// locale 告警只发一次（⛔ 不重复刷控制台）
let flatpickrLocaleWarned = false;
let flatpickrWatchStarted = false;

// locale 自检: 本包 vendor 那份 flatpickr 是**中文定制构建**
//   (l10ns.en / l10ns.default 的 months.shorthand[0] === '一月')
//   ⇒ 万一哪天被换成 upstream 官方构建, 日期选择器会静默变英文 —— 这里报一句中文警告。
//   满足条件 ⇒ 静默, 不打任何日志 (⛔ 不污染正常用户的控制台)。
function checkFlatpickrLocale() {
  try {
    const l10ns = window.flatpickr && window.flatpickr.l10ns;
    const shorthand = l10ns && l10ns.default && l10ns.default.months && l10ns.default.months.shorthand;
    if (!shorthand || shorthand[0] !== '一月') {
      if (flatpickrLocaleWarned) return;
      flatpickrLocaleWarned = true;
      console.warn('[ziven-core] flatpickr 不是中文定制构建 ⇒ 日期选择器可能静默变英文。');
    }
  } catch (e) {
    console.warn('[ziven-core] flatpickr locale 自检失败:', e);
  }
}

// zh.js 幂等 + 自愈（合并成一条判定：**当前实例上真的挂了 l10ns.zh** 才算就绪）
function ensureFlatpickrLocaleZh() {
  const fp = window.flatpickr;
  // ⛔ flatpickr 还没就绪 ⇒ 绝不插 zh.js
  //    (zh.js 早于 flatpickr.js 执行 ⇒ UMD 挂临时对象 ⇒ l10ns.zh 静默丢失)
  if (!fp) return;

  // 幂等 + 自愈合并成一条判定: 当前实例上真的挂了 zh 才算就绪
  //   ⇒ 实例被换掉后旧实例上的 zh 随实例一起消失 ⇒ 这里自然判为「未就绪」⇒ 允许重挂
  if (fp.l10ns && fp.l10ns.zh) {
    checkFlatpickrLocale();
    return;
  }
  if (flatpickrZhLoading) return;
  flatpickrZhLoading = true;
  const zh = document.createElement('script');
  zh.charset = 'utf-8';
  zh.src = FLATPICKR_ZH_JS;
  zh.onload = function () {
    flatpickrZhLoading = false;
    // ⚠️ 以「zh 是否真的挂到**当前**实例上」为准，而不是「脚本是否加载成功」
    checkFlatpickrLocale();
  };
  zh.onerror = function () {
    flatpickrZhLoading = false;
    console.warn('[ziven-core] flatpickr 中文语言包 zh.js 加载失败 ⇒ 日期选择器可能静默变英文。');
  };
  document.head.appendChild(zh);
}

// 自愈触发器: **只在**「当前实例上还没有 zh」时才补挂
//   ⛔ 绝不提前插 zh.js —— 提前插会让 zh.js 早于 flatpickr.js 执行,
//     l10ns.zh 挂到临时对象上**静默丢失**（那正是本次要修的 bug 本身）。
function healFlatpickrLocaleZh() {
  const fp = window.flatpickr;
  // 实例还没就绪 ⇒ 等 flatpickr.js 的 onload 来触发
  if (!fp) return;
  // 当前实例已经挂好了 ⇒ 无需补
  if (fp.l10ns && fp.l10ns.zh) return;
  if (flatpickrZhLoading) return;
  ensureFlatpickrLocaleZh();
}

// 盯住 <head> 里新加的 <script>: 有人又加载了一次 flatpickr.js ⇒ 实例被换 ⇒ 把 zh 补回去
//   - 内联文本注入 (scheduler 的同步 XHR + script.text 那种) 在 appendChild 时就执行完了
//     ⇒ observer 回调里直接补
//   - 外链 <script src> 的执行在 load 事件之后 ⇒ 挂个 load 监听再补一次
function watchFlatpickrReinjections() {
  if (flatpickrWatchStarted) return;
  flatpickrWatchStarted = true;
  if (!window.MutationObserver) return;
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      Array.prototype.forEach.call(m.addedNodes, function (node) {
        if (!node || node.tagName !== 'SCRIPT') return;
        const src = node.getAttribute('src') || '';
        const text = node.textContent || '';
        if (src.indexOf('flatpickr') === -1 && text.indexOf('flatpickr') === -1) return;
        healFlatpickrLocaleZh();
        if (src) {
          node.addEventListener('load', function () {
            healFlatpickrLocaleZh();
          });
        }
      });
    });
  });
  observer.observe(document.head, {
    childList: true
  });
  // 收尾再查一次: 万一有包用 eval / new Function 这种不经过 <script> 标签的方式换掉了实例
  window.addEventListener('load', function () {
    ensureFlatpickrLocaleZh();
  });
}

// flatpickr 统一入口
function ensureFlatpickrLoaded() {
  watchFlatpickrReinjections();
  // ① 别人（另一个包）已经加载好了 ⇒ 不再重复加载
  //   (重复执行 flatpickr.js 会整体重置 window.flatpickr.l10ns, 把 zh 冲掉)
  if (typeof window.flatpickr !== 'undefined' && window.flatpickr !== null) {
    // 但 l10ns.zh 仍要保证存在 —— 别人加载好了不代表挂了中文语言包
    ensureFlatpickrLocaleZh();
    return;
  }
  const fp = document.createElement('script');
  fp.charset = 'utf-8';
  fp.src = FLATPICKR_JS;
  fp.onload = function () {
    // ② flatpickr.js 执行完之后才插 zh.js (早于它执行 ⇒ l10ns.zh 静默丢失)
    ensureFlatpickrLocaleZh();
  };
  fp.onerror = function () {
    console.warn('[ziven-core] flatpickr.js 加载失败。');
  };
  document.head.appendChild(fp);
}
app.initializers.add('ziven-core', () => {
  // (辉哥 2026-10-24 22:0x 拍板 B 方案): 弹窗点遮罩不关闭
  //   装在基类 dismissibleOptions getter 上, **惰性读 setting** (app.forum 在
  //   initializers 阶段还没赋值 ⇒ 不能在这里读)。详见 modalBackdropClose.js。
  installModalBackdropClose(app);

  //   在 app boot 完成后调一次, 也监听 mithril redraw 重新应用 (防止 vendor 重建 <head> 抹掉)
  //
  //   修法: app.forum 在 IIFE 第一次跑时还没 ready (ForumState 异步 load),
  //   之前 try/catch 兜底但 console.warn 'Cannot read properties of undefined (reading attribute)'
  //   现在改成 silent: app.forum undefined 时直接 return, 不抛错也不 console.warn
  injectScrollbarStyle();
  setTimeout(injectScrollbarStyle, 100);
  setTimeout(injectScrollbarStyle, 1000);
  // 防止 vendor 重建 head 时 style 标签被清掉
  if (window.MutationObserver) {
    new MutationObserver(() => {
      if (!document.getElementById('ziven-core-scrollbar-style')) {
        injectScrollbarStyle();
      }
    }).observe(document.head, {
      childList: true
    });
  }

  // 预加载 FancyBox (静默加载，不阻塞页面)
  loadFancyBox();

  // (Trello 卡 6a7e65df8eb1684c9d8b78f8): 加载手机号登录 extension
  // (覆盖 vendor LogInModal 加 phone tab 切换, 辉哥 08:47 拍板 2A extend 模式)
  phoneLoginExtension();

  // (Trello 卡 6a7fc343f990e73904af2c75): 加载手机号注册 extension
  // (覆盖 vendor SignUpModal 加 phone tab 切换, 跟验证码登录/密码登录模式一致)
  signUpExtension();

  // (Trello 卡 6a7fcee8778d13c624d2acdc, 辉哥 11:51 拍板 go): 加载手机号找回密码 extension
  // (覆盖 vendor ForgotPasswordModal 加 phone tab 切换 + 子 tab 验证码/旧密码, 跟验证码登录/密码登录/注册模式一致)
  forgotPasswordExtension();

  // (Mavis 9/17 15:47 反馈 V 测仍 FAIL, debug 发现真因):
  //   r.18a V 测 Scenario 4 FAIL 真因是 L236 (改前) `User.prototype.ignore_all = ...`
  //   在 boot 时抛 TypeError `Cannot read properties of undefined (reading 'prototype')`,
  //   阻塞整个 initializer IIFE 后续执行 (Flarum 不自动 try/catch), 包括 hero extend 没注册,
  //   所以 `discussion.attribute('heroImageUrl')` 调用从未执行, 头图/overlay 永远不渲染
  //
  //   修法 A: try/catch 兜底 (避免阻塞后续 extends, 任何 ziven 扩展通用 SOP)
  //   修法 B: 用 extend() utility 延迟 patch, 不直接修改 vendor prototype
  //           (Flarum 推荐 pattern, 安全 deferred 到 User module ready 后)
  //
  //   实战教训沉淀 (CHANGELOG 跨 ziven 扩展适用):
  //   - Flarum 2.0 boot 时如果 vendor class 没 ready, 直接 `User.prototype.xxx = ...` 抛错
  //   - 这个错误阻塞整个 initializer IIFE, 后续 extends 全部跳过
  //   - 永远用 `extend()` utility 或 `try/catch` 包裹 prototype 修改
  try {
    User.prototype.ignore_all = Model.attribute('ignore_all');
  } catch (e) {
    // 兜底: User module 没 ready, 直接修改 prototype 失败, 不要阻塞后续 extends
    console.warn('[ziven-core] User.prototype.ignore_all patch failed:', e && e.message);
  }

  // 移除 ziven-core 对 /search 的自定义覆盖 (辉哥拍板「不要自定义搜索页了」).
  //   背景: coreSearchPage.js 是照抄 Flarum 1.x 搜索页写的, 升级 Flarum 2.0 后
  //   coreSearchPage.js:41 调 IndexPage.prototype.sidebarItems() —— 2.0 的 IndexPage
  //   已无此方法 (搬到 PageStructure.tsx), 于是渲染阶段抛 TypeError ⇒ /search 白屏.
  //   同时删除 js/src/forum/coreSearchPage.js 与 components/searchRelevantComponent.js.
  //
  //   ⚠️ Flarum 2.0 **没有** /search 路由 (2.0 把搜索改成了 HeaderSecondary 里的
  //   GlobalSearch 按钮 → 弹 SearchModal, 不是独立页面). 所以这里的「接管」不是
  //   换回一个原生搜索页, 而是:
  //     · 前端: /search 不再匹配任何 mithril 路由 → 按 mithril 默认行为落到首页
  //       (见 vendor/flarum/core Application.tsx mount(): m.route(el, basePath+'/', routes))
  //     · 后端: extend.php 仍保留 PHP 路由 /search → CoreSearchController (返回标准
  //       Document), 所以 /search 是 200 而不是服务器 404.
  //   即论坛不再有自定义搜索页, 搜索改用 Flarum 2.0 原生的搜索弹窗 (讨论/帖子/用户 3 个 tab).

  // 注册 Fancybox 工具函数
  app.fancyboxOpen = function (src, options) {
    if (options === void 0) {
      options = {};
    }
    openFancyBox(src, options);
  };
  safeExtend(Modal, 'onready', function (params) {
    $(".ModalManager-invisibleBackdrop").off("click");
  });

  // Trello 卡 6a92e0d646d27a832fb339ea (辉哥 8/29 21:35 调研确认, 21:37 拍板修法 A):
  //   根因: iOS 14+ modal 打开瞬间页面跳顶部. vendor FormModal.onready 触发 input focus
  //     让 iOS 14+ input focus + position:fixed body 行为互相抵消, scrollY 锁定在 input
  //     位置 (modal 中间). BookingModal extends Modal (不 extends FormModal), Modal.onready
  //     是空, iOS 14+ body-scroll-lock 库 iOS 13+ technique 失效把 scrollY reset 到 0.
  //   修法: 全局 override Modal.prototype.onready 强制所有 modal 触发首个 input focus,
  //     跟 LogInModal 路径一致. 跨项目覆盖所有 modal (LogInModal / SignUpModal /
  //     ForgotPasswordModal / TagSelectionModal / BookingModal / 任何第三方 modal).
  //   安全性: LogInModal 已有 FormModal.onready 触发 focus, 我们的 override 跑 super (空) +
  //     我们的 focus, 双重 focus 不会报错 (jQuery trigger focus 重复触发 noop).
  //   保留既有兑底链 (animateHide 3 rAF + ModalManagerState.close 4 rAF
  //     + setTimeout 50/200/1000 + documentElement.scrollTop).
  safeOverride(Modal, 'onready', function (original) {
    original(); // Modal.onready 是空, FormModal 子类会调到 FormModal.onready (input focus)
    // (ziven-scheduler 旧版本残留): 用户已经在这个 modal 里落过手 ⇒ 不再抢焦点。
    //   上面 L387-398 的 iOS 14+ 修法**保留不动**, 本轮只收窄「已经交互过」这一种情形;
    //   未交互时下面这 5 行与旧版逐字节一致 (B2 首个 input 照旧被聚焦 + select)。
    if (this.__zivenCoreUserTouched) return;
    // 跨项目统一: 强制所有 modal 触发首个 input focus, 跟 LogInModal 路径一致
    // 修 iOS 14+ body-scroll-lock 库 iOS 13+ technique 失效导致 modal 打开瞬间 scrollY reset 到 0
    const $el = this.$ && this.$();
    if ($el && $el.find) {
      const $input = $el.find('input, select, textarea').first();
      if ($input.length) {
        $input.trigger('focus').trigger('select');
      }
    }
  });

  // — 「用户碰过这个 modal」守卫的**挂载点**。必须在 oncreate, 不能写在上面
  //   那个 onready 里面 —— 那是本题最容易静默失败的地方:
  //     vendor ModalManager.animateShow (ModalManager.tsx:181) 把 onready 挂在 `.Modal` 的
  //     `transitionend` 上, 实测 modal 挂载 → transitionend ≈ 167~262ms (B3 量到的 select()
  //     落点)。若把「记录用户交互」的监听写进 onready, 它挂上去时用户的第一次 mousedown
  //     早就发生过了 ⇒ 守卫恒为「未交互」⇒ 代码看着对、实际一点用没有, 而且**不报错**。
  //   为什么 oncreate 一定赶得上:
  //     vendor Modal.oncreate (Modal.tsx:62-66) 同步跑完 super.oncreate + attrs.animateShow(...);
  //     animateShow 只是挂 transitionend 监听器, 再把「加 'in' 类」推到**下一个 rAF**
  //     (ModalManager.tsx:190) —— 过渡最早也要下一帧才开始, transitionend 更在其后。
  //     extend() 的约定是 original 先跑、callback 后跑
  //     (vendor/flarum/core/js/src/common/extend.ts:51 先调 original、:54 后调 callback), 所以本 callback
  //     与 animateShow 在**同一个同步 tick** 内完成 ⇒ 早于 rAF ⇒ 早于 transitionend。
  //   为什么挂在**每个 modal 自己的根元素** (vnode.dom, 即 `.Modal` 那层) 而不是 document:
  //     作用域天然只覆盖本 modal, 别的 modal / 页面不受影响; 也省掉「document 级记录 +
  //     onready 时反查是哪个 modal」那套要靠 class 名反查的脆弱写法。
  //   事件集为什么只有这四个:
  //     · 不监听 focusin —— ModalManager 建焦点陷阱 (ModalManager.tsx:127) 时可能自己产生
  //       一次 modal 内的 focusin, 那不是用户交互; 记进去会让「完全不碰」也被判成交互过,
  //       等于把 8/29 那个 iOS bug 放回来。
  //     · 不监听 input —— keydown 必然先于 input, 且 input 也可能由扩展 autofill 触发。
  //     · 不监听 click —— 点击必然先有 pointerdown/mousedown, 重复记没有额外信息。
  //     · keydown 覆盖 Tab 移进输入框: trusted keydown 必先于 focus 到达 (ziven-scheduler
  //       SearchableUserPicker.js:154-157 实测 keydown → focus 间隔 2.7ms)。
  safeExtend(Modal, 'oncreate', function (vnode) {
    const el = this.element || vnode && vnode.dom;
    if (!el || typeof el.addEventListener !== 'function' || this.__zivenCoreUserTouched) return;
    const mark = () => {
      this.__zivenCoreUserTouched = true;
    };
    ['pointerdown', 'mousedown', 'touchstart', 'keydown'].forEach(type => {
      el.addEventListener(type, mark, true);
    });
  });

  // 删 pull-to-refresh-js init
  // 1.x 时代遗留, 微信 X5 下拉触发 preventDefault → onRefresh → location.reload()
  // → 视觉上"松手弹回顶部" (辉哥 11:32 反馈, 拍板 A 方案删除)
  // mithril 2.x 也不需要 pull-to-refresh UI, Flarum 自带 nav 已经是 SPA

  modifyApplication();
  modifyHeaderSecondary();
  modifyNavigation();
  modifyDiscussionItem();
  modifyTagSelectionModal();
  modifyDrawer();
  modifyUserPage();

  // v2 (Trello 卡 F3b3PgCM, 辉哥 9/19 17:30 调研真因诊断):
  //   === priority -100 initializer 注册必须在 MODULE-LOAD TOP LEVEL 跟 'ziven-core'
  //   initializer 并列注册, 不能塞进 'ziven-core' initializer callback 内部 ===
  //   原因 (Mavis 9/19 17:30 调研): Flarum Application.initialize() 第一次跑时调
  //   `this.initializers.toArray()` 拿快照排序 by priority 高→低, 之后 forEach 跑
  //   数组. 在 'ziven-core' callback 内 add() 注册的新 initializer (priority -100) 只
  //   更新 this._items 字典, 不会更新已经快照的 toArray 数组. 内部 add() 永远不被跑.
  //   修法: 跟 'ziven-core' initializer 一样在 module-load top level 注册, 这样两
  //   者都在 ItemList 快照前, sorted array 按 priority 0 → -100 顺序跑.

  // (Trello 卡 6aab90b1e1dc16fd9ba865c0): 注册头图功能
  extendDiscussionPage();
  modifyDiscussionComposer();
  // (辉哥 2026-09-23 08:38 拍板 a 方案, Trello 卡 trFWS1CK / 6ab31fb50aa966f6f853935c):
  //   override vendor TerminalPost view() 末尾追加最后回复用户头像 + 用户链接
  modifyTerminalPost();
  /** 用户搜索 黑名单过滤 */
  IgnoreSearchUser();
  /** 富文本 推荐名单过滤  */
  IgnoreMentions();

  // let calendarTranApi = document.createElement('script');
  // calendarTranApi.charset = 'utf-8';
  // calendarTranApi.src = '/assets/extensions/ziiven-ziven-core/calender/calendar.translations.zh.js';
  // document.head.appendChild(calendarTranApi);

  // let calendarApi = document.createElement('script');
  // calendarApi.charset = 'utf-8';
  // calendarApi.src = '/assets/extensions/ziiven-ziven-core/calender/calendar.min.js';
  // document.head.appendChild(calendarApi);

  const head = document.getElementsByTagName('head')[0];
  // const calendarLink  = document.createElement('link');
  // calendarLink.rel  = 'stylesheet';
  // calendarLink.type = 'text/css';
  // calendarLink.href = '/assets/extensions/ziiven-ziven-core/calender/calendar.js.min.css';
  // calendarLink.media = 'all';
  // head.appendChild(calendarLink);

  // flatpickr: 走统一加载器 (先探测已存在没有 → 已存在就跳过, 之后按顺序补 zh.js)
  //   ⛔ 不再无条件 appendChild —— 无条件注入 = 每次都整体重置 window.flatpickr.l10ns
  ensureFlatpickrLoaded();
  const flatpickrLink = document.createElement('link');
  flatpickrLink.rel = 'stylesheet';
  flatpickrLink.type = 'text/css';
  flatpickrLink.href = '/assets/extensions/ziiven-ziven-core/flatpickr/flatpickr.min.css';
  flatpickrLink.media = 'all';
  head.appendChild(flatpickrLink);
  window.onpopstate = history.onpushstate = function (e) {
    app.composer.hide();
  };
  $.fn.isInViewport = function () {
    const elementTop = $(this).offset().top;
    const elementBottom = elementTop + $(this).outerHeight();
    const viewportTop = $(window).scrollTop();
    const viewportBottom = viewportTop + $(window).height();
    return elementBottom > viewportTop && elementTop < viewportBottom;
  };
  $(window).on('resize scroll', function () {
    $('[class$="loadMore"] button').each(function () {
      if ($(this).isInViewport()) {
        $(this).click();
      }
    });
  });

  // (辉哥 2026-09-05 18:07 反馈 "在Ziven core中加入, 让访问者无法选择及复制页面中的文字"):
  //   阻止访问者通过鼠标/键盘选择/复制页面文字 (防搬运/反爬虫)
  //   视觉层 (forum.less user-select: none) + 事件层 (本段) 双重防护
  //   允许 input/textarea/contenteditable 等可编辑元素正常选择 (用户输入 + Flarum Composer)
  //   允许 a/img/video/button/[role=button] 元素的右键菜单 (图片复制 + 链接打开)
  //   实战教训 (SOP 33x): 反爬虫通常用 user-select: none + copy 阻止 即可
  //     - 拖拽阻止 (dragstart) 跟 selectstart 是另一类 (图片拖拽保护), 暂不开启
  //     - contextmenu 不全局阻止, 否则开发者工具跟查看源码都受影响, 只在普通正文区阻止
  (function protectContent() {
    // 标记"可选择"的元素 (白名单), 适用于所有阻止事件
    const isSelectable = function (target) {
      if (!target) return false;
      if (target.matches && target.matches('input, textarea, [contenteditable="true"], [contenteditable=""]')) return true;
      if (target.closest && target.closest('[contenteditable="true"]')) return true;
      // 代码块 / 调试元素 (开发/技术内容) 允许选择 (Flarum 自身 .Post-body code / pre)
      if (target.closest && target.closest('code, pre, .Composer, .TextEditor, .FormControl, .Search-input')) return true;
      return false;
    };
    // 阻止 copy / cut (除白名单元素)
    ['copy', 'cut'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        if (isSelectable(e.target)) return;
        e.preventDefault();
      }, {
        passive: false
      });
    });
    // 阻止 selectstart (除白名单元素) — 阻止鼠标拖动选择
    document.addEventListener('selectstart', function (e) {
      if (isSelectable(e.target)) return;
      e.preventDefault();
    }, {
      passive: false
    });
    // 阻止 contextmenu (除白名单元素 + a/img/video/button) — 防止右键菜单显示"复制"
    document.addEventListener('contextmenu', function (e) {
      if (isSelectable(e.target)) return;
      // 允许 a/img/video/button 等元素右键 (图片复制 / 链接打开)
      if (e.target.closest && e.target.closest('a, img, video, button, [role="button"], input, textarea')) return;
      e.preventDefault();
    }, {
      passive: false
    });
  })();
});
export * from '../common';